import { z } from 'zod';
import { MERCHANT_ROLES } from '../types/enums.js';
import { emailSchema, malaysianStateSchema, merchantSlugSchema, moneySchema, optionalMoneySchema, optionalPhoneSchema, uuidSchema, } from './common.js';
export const updateMerchantSettingsSchema = z.object({
    name: z.string().trim().min(2).max(160).optional(),
    slug: merchantSlugSchema.optional(),
    supportEmail: emailSchema.nullable().optional(),
    supportPhone: optionalPhoneSchema,
    timezone: z.string().trim().min(3).max(64).optional(),
    currency: z.literal('MYR').optional(),
    /** SST rate in whole percent, e.g. "6". */
    taxRatePercent: z
        .string()
        .regex(/^\d{1,2}(\.\d{1,2})?$/, 'Enter a valid tax rate')
        .optional(),
    taxAppliesToShipping: z.boolean().optional(),
    /** Master switch for "an order was paid" emails to the store's own team. */
    notifyOrderEmails: z.boolean().optional(),
    storefrontHeaderBorder: z.boolean().optional(),
    storefrontFooterPanel: z.boolean().optional(),
    storefrontBackToTop: z.boolean().optional(),
    registrationNumber: z.string().trim().max(50).nullable().optional(),
    addressLine1: z.string().trim().max(200).nullable().optional(),
    addressLine2: z.string().trim().max(200).nullable().optional(),
    city: z.string().trim().max(100).nullable().optional(),
    state: z.string().trim().max(60).nullable().optional(),
    postcode: z.string().trim().max(10).nullable().optional(),
    logoUrl: z.string().url().max(2048).nullable().optional(),
    brandColour: z
        .string()
        .regex(/^#[0-9a-fA-F]{6}$/, 'Choose a valid colour')
        .nullable()
        .optional(),
});
/** Staff invitations. The invited user may or may not already have an account. */
export const inviteStaffSchema = z.object({
    email: emailSchema,
    name: z.string().trim().min(2).max(120).optional(),
    role: z.enum(MERCHANT_ROLES).refine((r) => r !== 'OWNER', {
        message: 'Ownership is transferred separately and cannot be invited',
    }),
});
export const acceptStaffInvitationSchema = z.object({
    token: z.string().min(16).max(256),
    name: z.string().trim().min(2).max(120).optional(),
    password: z.string().min(10).max(128).optional(),
});
export const updateStaffRoleSchema = z.object({
    role: z.enum(MERCHANT_ROLES).refine((r) => r !== 'OWNER', {
        message: 'Ownership is transferred separately',
    }),
});
/** Opt a team member in or out of receiving a copy of order emails. */
export const updateStaffNotificationsSchema = z.object({
    receivesOrderEmails: z.boolean(),
});
/** Shipping configuration. */
export const createShippingZoneSchema = z.object({
    name: z.string().trim().min(2).max(80),
    /** Malaysian state codes covered by the zone. Empty means "everywhere else". */
    stateCodes: z.array(malaysianStateSchema).max(16).default([]),
    isActive: z.boolean().default(true),
});
export const updateShippingZoneSchema = createShippingZoneSchema.partial();
export const createShippingMethodSchema = z.object({
    zoneId: uuidSchema,
    name: z.string().trim().min(2).max(80),
    description: z.string().trim().max(300).nullable().optional(),
    rate: moneySchema,
    /** Orders at or above this subtotal ship free. */
    freeShippingThreshold: optionalMoneySchema,
    estimatedDaysMin: z.number().int().min(0).max(90).nullable().optional(),
    estimatedDaysMax: z.number().int().min(0).max(90).nullable().optional(),
    isActive: z.boolean().default(true),
    position: z.number().int().min(0).default(0),
});
export const updateShippingMethodSchema = createShippingMethodSchema.partial().omit({ zoneId: true });
// -----------------------------------------------------------------------------
// Payment methods
// -----------------------------------------------------------------------------
/**
 * Save a payment method's setup. A secret left blank keeps what is already saved,
 * so editing one field never forces re-entering every key.
 */
export const savePaymentMethodSchema = z.object({
    values: z.record(z.string().max(64), z.string().max(430_000)).default({}),
    mode: z.enum(['sandbox', 'live']).default('sandbox'),
});
export const enablePaymentMethodSchema = z.object({
    enabled: z.boolean(),
    makeDefault: z.boolean().optional(),
});
export const startUpgradeSchema = z.object({
    planId: z.string().uuid(),
    interval: z.enum(['MONTHLY', 'YEARLY']),
});
//# sourceMappingURL=merchant.js.map